import type { ApiResponse, MindMapNode, SurveyRecord, InterviewRecord } from '@bandos/shared'
import type { ReviewedBatch } from '../components/BatchQuestionReview'
import { MindmapSurveyService } from '../../survey/services/mindmapSurveyService'
import { MindmapInterviewService } from '../../interview/services/mindmapInterviewService'

/**
 * Persist a reviewed validation batch as a standalone survey or interview row.
 *
 * Picks an anchor parent node from the mindmap (the venture/need/idea node
 * the validation is attached to). Anchor priority: venture > first selected
 * node > first node in the mindmap. Throws if the mindmap has no nodes.
 *
 * Replaces the previous flow that built an in-memory mindmap node + edge and
 * then saved the whole mindmap.
 */
export async function createValidationRecord(
  batch: ReviewedBatch,
  mindmapId: string,
  nodes: MindMapNode[],
  selectedNodeIds: string[]
): Promise<ApiResponse<{ id: string }>> {
  const venture = nodes.find((n) => n.data.category === 'venture')
  const anchor = venture ?? nodes.find((n) => selectedNodeIds.includes(n.id)) ?? nodes[0]

  if (!anchor) {
    throw new Error('Cannot create validation record: mindmap has no nodes to anchor to.')
  }

  const anchorLabel = (anchor.data.label as string | undefined) ?? 'Untitled'
  const anchorCategory = (anchor.data.category as string | undefined) ?? 'venture'

  if (batch.method === 'survey') {
    const res = await MindmapSurveyService.create(mindmapId, {
      parentNodeId: anchor.id,
      parentNodeCategory: anchorCategory,
      parentLabel: anchorLabel,
      selectedNodeIds: selectedNodeIds.length > 0 ? selectedNodeIds : undefined,
      label: batch.name,
      description: batch.description || undefined,
      questions: batch.questions,
    })
    return wrap<SurveyRecord>(res, 'survey')
  }

  const res = await MindmapInterviewService.create(mindmapId, {
    parentNodeId: anchor.id,
    parentNodeCategory: anchorCategory,
    parentLabel: anchorLabel,
    selectedNodeIds: selectedNodeIds.length > 0 ? selectedNodeIds : undefined,
    label: batch.name,
    description: batch.description || undefined,
    questions: batch.questions,
  })
  return wrap<InterviewRecord>(res, 'interview')
}

function wrap<R extends SurveyRecord | InterviewRecord>(
  res: ApiResponse<{ survey: R } | { interview: R }>,
  kind: 'survey' | 'interview'
): ApiResponse<{ id: string }> {
  if (res.error || !res.data) {
    return { data: null, error: res.error ?? { message: `Failed to create ${kind}.` } }
  }
  const record =
    kind === 'survey'
      ? (res.data as { survey: R }).survey
      : (res.data as { interview: R }).interview
  return { data: { id: record.id }, error: null }
}
